<template>
  <SidePanel :open="open" :title="panelTitle" @close="$emit('close')">
    <template v-if="entry">
      <p class="hp-body">{{ entry.body }}</p>
      <ul v-if="entry.bullets && entry.bullets.length" class="hp-bullets">
        <li v-for="(b, i) in entry.bullets" :key="i">{{ b }}</li>
      </ul>
      <p class="hp-context">
        <span class="hp-context__label">Section:</span>
        <span class="hp-context__value">{{ sectionTitle }}</span>
      </p>
    </template>

    <template v-else>
      <div class="hp-empty">
        <p class="hp-empty__copy">No help content for this section yet — but you can ask in the team chat any time.</p>
      </div>
    </template>

    <template #footer>
      <div class="hp-footer">
        <a
          v-if="entry?.link"
          :href="entry.link.url"
          class="hp-footer__link"
          @click="onLinkClick"
        >
          <span>{{ entry.link.label }}</span>
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M5 12 L19 12" />
            <path d="M13 6 L19 12 L13 18" />
          </svg>
        </a>
        <span v-else class="hp-footer__hint">Press <kbd>?</kbd> any time to reopen this panel.</span>
      </div>
    </template>
  </SidePanel>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { helpFor, type HelpEntry } from '@/config/help.config'
import SidePanel from './SidePanel.vue'

export default defineComponent({
  name: 'HelpPanel',
  components: { SidePanel },
  props: {
    open: { type: Boolean, default: false },
  },
  emits: ['close'],
  setup(_, { emit }) {
    const route = useRoute()
    const router = useRouter()

    const entry = computed<HelpEntry | undefined>(() =>
      helpFor(route.meta.helpKey as string | undefined)
    )
    const sectionTitle = computed<string>(() => (route.meta.sectionTitle as string) || '')
    const panelTitle = computed<string>(() =>
      entry.value ? `Help — ${entry.value.title}` : 'Help'
    )

    function onLinkClick(e: MouseEvent) {
      const url = entry.value?.link?.url
      if (!url) return
      if (url.startsWith('/')) {
        e.preventDefault()
        emit('close')
        router.push(url)
      }
    }

    return { entry, panelTitle, sectionTitle, onLinkClick }
  },
})
</script>

<style scoped>
.hp-body {
  margin: 0 0 16px;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.85);
}

.hp-bullets {
  margin: 0 0 20px;
  padding: 0 0 0 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.hp-bullets li {
  font-size: 13px;
  font-weight: 300;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.78);
}
.hp-bullets li::marker {
  color: #96ffc9;
}

.hp-context {
  margin: 16px 0 0;
  padding: 12px;
  border-radius: 12px;
  background: rgba(150, 255, 201, 0.06);
  border: 1px solid rgba(150, 255, 201, 0.18);
  font-size: 12px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  gap: 8px;
}
.hp-context__label {
  color: rgba(255, 255, 255, 0.45);
}
.hp-context__value {
  color: #96ffc9;
  font-weight: 700;
}

.hp-empty {
  text-align: center;
  padding: 48px 16px;
  color: rgba(255, 255, 255, 0.55);
}
.hp-empty__copy {
  font-size: 13px;
  font-weight: 300;
  line-height: 1.5;
  max-width: 260px;
  margin: 0 auto;
}

.hp-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.hp-footer__link {
  font-size: 13px;
  font-weight: 700;
  color: #96ffc9;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.hp-footer__link:hover {
  text-decoration: underline;
}
.hp-footer__link svg {
  flex-shrink: 0;
}
.hp-footer__hint {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 300;
}
.hp-footer__hint kbd {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 10px;
  font-family: var(--font-sans);
  color: rgba(255, 255, 255, 0.7);
}
</style>
